/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import LinearProgress from '@mui/material/LinearProgress';
import {
  DataGrid,
  DataGridProps,
  esES,
  enUS,
  GridColumns,
  GridRowsProp,
  GridSlotsComponent
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

type Props<T> = Partial<DataGridProps<T>> & {
  tableName: string;
  columns: GridColumns<T>;
  rows: GridRowsProp<T>;
  customComponents?: Partial<GridSlotsComponent>;
  moreStyles?: any;
  autoHeight?: boolean;
};

export default function GenericDataGrid<T>({
  tableName,
  customComponents,
  moreStyles,
  autoHeight = true,
  hideFooter = true,
  ...restOfProps
}: Props<T>) {
  const { i18n } = useTranslation('global');
  return (
    <DataGrid
      autoHeight={autoHeight}
      components={{
        LoadingOverlay: LinearProgress,
        ...customComponents
      }}
      componentsProps={{
        toolbar: {
          printOptions: { hideFooter: true, hideToolbar: true },
          csvOptions: {
            fileName: `${tableName}-${dayjs().format()}`,
            utf8WithBom: true
          }
        }
      }}
      density="comfortable"
      getEstimatedRowHeight={() => 70}
      getRowHeight={() => 'auto'}
      headerHeight={39}
      hideFooter={hideFooter}
      localeText={
        i18n.language === 'es'
          ? esES.components.MuiDataGrid.defaultProps.localeText
          : enUS.components.MuiDataGrid.defaultProps.localeText
      }
      sx={{
        ...moreStyles,
        '.MuiDataGrid-columnHeaderTitleContainer': {
          justifyContent: 'center'
        },
        '.MuiDataGrid-columnHeaders': {
          textAlign: 'center',
          backgroundColor: '#0B2E6A',
          color: 'white',
          '.MuiSvgIcon-root': {
            fill: 'white'
          }
        },
        '.MuiLinearProgress-root': {
          height: '8px'
        },
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
          outline: 'none'
        },
        '& .MuiDataGrid-row': {
          borderTop: 1
        },
        '.MuiDataGrid-cell': {
          padding: '10px 10px 10px 10px ',
          justifyContent: 'center',
          textAlign: 'center'
        },
        '.MuiStack-root': {
          '@media (max-width: 750px)': {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
            marginTop: '.5em'
          }
        }
      }}
      disableColumnFilter
      disableColumnMenu
      disableSelectionOnClick
      keepNonExistentRowsSelected
      {...restOfProps}
    />
  );
}
